import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { 
  Calendar, Dumbbell, Zap, Clock, ChevronDown, ChevronUp,
  X, Loader2, Play, CheckCircle, Target, TrendingUp,
  AlertCircle, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../config/api';

export default function MesocyclePlanViewer({ childId, childName, planId, onClose, onPlanGenerated }) {
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [plan, setPlan] = useState(null);
  const [progress, setProgress] = useState(null);
  const [expandedWeeks, setExpandedWeeks] = useState({ 1: true, 2: false, 3: false, 4: false });
  const [expandedSessions, setExpandedSessions] = useState({});
  const [hasProfile, setHasProfile] = useState(false);

  useEffect(() => {
    if (planId) {
      fetchPlan();
    } else {
      checkForProfile();
    }
  }, [planId, childId]);

  const checkForProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/profiling/athlete/${childId}/latest-profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHasProfile(!!response.data.profile);
      
      // Also check for existing plans
      const plansResponse = await axios.get(`${API}/mesocycle/athlete/${childId}/plans?status=active`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (plansResponse.data.plans?.length > 0) {
        setPlan(plansResponse.data.plans[0]);
        fetchProgress(plansResponse.data.plans[0].id);
      }
    } catch (err) {
      console.error('Error checking profile:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchPlan = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/mesocycle/plan/${planId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPlan(response.data);
      fetchProgress(planId);
    } catch (err) {
      console.error('Failed to fetch plan:', err);
      toast.error('Failed to load training plan');
    } finally {
      setLoading(false);
    }
  };

  const fetchProgress = async (pid) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/mesocycle/plan/${pid}/progress`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProgress(response.data);
    } catch (err) {
      console.error('Failed to fetch progress:', err);
    }
  };

  const generatePlan = async () => {
    setGenerating(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/mesocycle/generate/${childId}`, {
        sessions_per_week: 3,
        session_duration_minutes: 60,
        equipment_available: ["barbell", "dumbbell", "bodyweight", "cable", "kettlebell"]
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('4-week mesocycle plan generated!');
      setPlan({ ...response.data, id: response.data.id, plan: response.data.plan });
      if (onPlanGenerated) onPlanGenerated(response.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate plan');
    } finally {
      setGenerating(false);
    }
  };

  const toggleWeek = (week) => {
    setExpandedWeeks(prev => ({ ...prev, [week]: !prev[week] }));
  };

  const toggleSession = (sessionId) => {
    setExpandedSessions(prev => ({ ...prev, [sessionId]: !prev[sessionId] }));
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#02b5ff]" />
      </div>
    );
  }

  // No plan exists - show generate option
  if (!plan) {
    return (
      <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
        <div 
          className="bg-zinc-900 border border-zinc-700 rounded-2xl w-full max-w-lg p-8 text-center"
          onClick={e => e.stopPropagation()}
        >
          <div className="w-16 h-16 bg-gradient-to-br from-[#02b5ff]/20 to-[#ff025f]/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Calendar className="h-8 w-8 text-[#02b5ff]" />
          </div>
          
          <h2 className="text-2xl font-bold text-white mb-2">4-Week Mesocycle Plan</h2>
          <p className="text-zinc-400 mb-6">{childName}</p>
          
          {!hasProfile ? (
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 mb-6">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" />
                <div className="text-left">
                  <p className="text-amber-200 font-medium">Profile Required</p>
                  <p className="text-sm text-amber-300/70 mt-1">
                    Complete a profiling assessment first to generate a personalized training plan.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 mb-6">
              <div className="flex items-center gap-3">
                <CheckCircle className="h-5 w-5 text-green-400" />
                <p className="text-green-200">Profile found - ready to generate plan</p>
              </div>
            </div>
          )}
          
          <p className="text-sm text-zinc-500 mb-6">
            The AI will analyze the athlete's profile and create a personalized 4-week training program
            targeting identified weaknesses while maintaining strengths.
          </p>
          
          <div className="flex gap-3">
            <Button variant="outline" onClick={onClose} className="flex-1 border-zinc-700">
              Cancel
            </Button>
            <Button 
              onClick={generatePlan}
              disabled={!hasProfile || generating}
              className="flex-1 bg-gradient-to-r from-[#02b5ff] to-[#ff025f] text-white disabled:opacity-50"
            >
              {generating ? (
                <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Generating...</>
              ) : (
                <><Zap className="h-4 w-4 mr-2" /> Generate Plan</>
              )}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Plan exists - show the full plan viewer
  const planData = plan.plan || plan;

  return (
    <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div 
        className="bg-zinc-900 border border-zinc-700 rounded-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-zinc-700">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-gradient-to-br from-[#02b5ff] to-[#ff025f] rounded-xl flex items-center justify-center">
              <Calendar className="h-6 w-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">4-Week Mesocycle Plan</h2>
              <p className="text-sm text-zinc-400">
                {childName} • {plan.start_date} to {plan.end_date}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {progress && (
              <div className="text-right mr-4">
                <p className="text-2xl font-bold text-[#02b5ff]">{progress.completion_percentage}%</p>
                <p className="text-xs text-zinc-400">Complete</p>
              </div>
            )}
            <Button 
              variant="ghost" 
              onClick={generatePlan}
              disabled={generating}
              className="text-zinc-400 hover:text-white"
            >
              <RefreshCw className={`h-4 w-4 ${generating ? 'animate-spin' : ''}`} />
            </Button>
            <button onClick={onClose} className="text-zinc-500 hover:text-white p-2">
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Plan Overview */}
          {planData.plan_overview && (
            <div className="bg-gradient-to-r from-[#02b5ff]/10 to-[#ff025f]/10 border border-[#02b5ff]/30 rounded-xl p-4 mb-6">
              <h3 className="font-semibold text-white mb-2 flex items-center gap-2">
                <Target className="h-5 w-5 text-[#02b5ff]" />
                Plan Overview
              </h3>
              <div className="grid md:grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="text-zinc-400">Goal</p>
                  <p className="text-white">{planData.plan_overview.goal}</p>
                </div>
                <div>
                  <p className="text-zinc-400">Phase</p>
                  <p className="text-white">{planData.plan_overview.phase}</p>
                </div>
                <div>
                  <p className="text-zinc-400">Progression</p>
                  <p className="text-white">{planData.plan_overview.progression_model}</p>
                </div>
              </div>
            </div>
          )}

          {/* Focus Areas */}
          {plan.focus_areas?.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-6">
              <span className="text-sm text-zinc-400">Focus areas:</span>
              {plan.focus_areas.map((area, idx) => (
                <span key={idx} className="px-3 py-1 bg-[#ff025f]/20 text-[#ff025f] rounded-full text-sm">
                  {area}
                </span>
              ))}
            </div>
          )}

          {/* Weekly Focus */}
          {planData.weekly_focus && (
            <div className="grid grid-cols-4 gap-3 mb-6">
              {[1, 2, 3, 4].map(week => (
                <div 
                  key={week}
                  className={`p-3 rounded-xl text-center cursor-pointer transition-all ${
                    expandedWeeks[week] 
                      ? 'bg-[#02b5ff]/20 border border-[#02b5ff]/50' 
                      : 'bg-zinc-800/50 border border-zinc-700/50 hover:bg-zinc-800'
                  }`}
                  onClick={() => toggleWeek(week)}
                >
                  <p className="font-bold text-white">Week {week}</p>
                  <p className="text-xs text-zinc-400 mt-1">
                    {week === 4 ? 'Deload' : week === 3 ? 'Peak' : week === 2 ? 'Build' : 'Foundation'}
                  </p>
                </div>
              ))}
            </div>
          )}

          {/* Training Sessions */}
          {planData.training_sessions && (
            <div className="space-y-4">
              <h3 className="font-semibold text-white flex items-center gap-2">
                <Dumbbell className="h-5 w-5 text-orange-400" />
                Training Sessions
              </h3>
              
              {planData.training_sessions.map((session, idx) => (
                <div key={idx} className="bg-zinc-800/50 rounded-xl border border-zinc-700/50 overflow-hidden">
                  <button
                    onClick={() => toggleSession(idx)}
                    className="w-full flex items-center justify-between p-4 hover:bg-zinc-800 transition-all"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        session.session_type?.includes('Strength') ? 'bg-orange-500/20' :
                        session.session_type?.includes('Power') ? 'bg-yellow-500/20' :
                        session.session_type?.includes('Speed') ? 'bg-red-500/20' :
                        'bg-blue-500/20'
                      }`}>
                        {session.session_type?.includes('Strength') ? <Dumbbell className="h-5 w-5 text-orange-400" /> :
                         session.session_type?.includes('Power') ? <Zap className="h-5 w-5 text-yellow-400" /> :
                         <Play className="h-5 w-5 text-blue-400" />}
                      </div>
                      <div className="text-left">
                        <p className="font-medium text-white">{session.day}</p>
                        <p className="text-sm text-zinc-400">{session.session_type}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-zinc-500">
                        {session.main_workout?.length || 0} exercises
                      </span>
                      {expandedSessions[idx] ? <ChevronUp className="h-5 w-5 text-zinc-400" /> : <ChevronDown className="h-5 w-5 text-zinc-400" />}
                    </div>
                  </button>
                  
                  {expandedSessions[idx] && (
                    <div className="p-4 border-t border-zinc-700/50 space-y-4">
                      {/* Warm-up */}
                      {session.warm_up?.length > 0 && (
                        <div>
                          <p className="text-xs text-zinc-500 mb-2 uppercase tracking-wider">Warm-up</p>
                          <div className="space-y-1">
                            {session.warm_up.map((ex, exIdx) => (
                              <div key={exIdx} className="flex items-center justify-between text-sm p-2 bg-zinc-900/50 rounded">
                                <span className="text-white">{ex.exercise}</span>
                                <span className="text-zinc-400">{ex.duration}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Main Workout */}
                      {session.main_workout?.length > 0 && (
                        <div>
                          <p className="text-xs text-zinc-500 mb-2 uppercase tracking-wider">Main Workout</p>
                          <div className="space-y-3">
                            {session.main_workout.map((ex, exIdx) => (
                              <div key={exIdx} className={`p-3 rounded-lg ${ex.targets_weakness ? 'bg-[#ff025f]/10 border border-[#ff025f]/30' : 'bg-zinc-900/50'}`}>
                                <div className="flex items-center justify-between mb-2">
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium text-white">{ex.exercise}</span>
                                    {ex.targets_weakness && (
                                      <span className="px-2 py-0.5 bg-[#ff025f]/20 text-[#ff025f] rounded text-xs">
                                        Priority
                                      </span>
                                    )}
                                  </div>
                                  <span className="text-xs text-zinc-500">{ex.category}</span>
                                </div>
                                
                                {/* Weekly progression */}
                                <div className="grid grid-cols-4 gap-2 text-xs">
                                  {[1, 2, 3, 4].map(week => {
                                    const weekData = ex[`week_${week}`];
                                    return weekData ? (
                                      <div key={week} className={`p-2 rounded ${expandedWeeks[week] ? 'bg-[#02b5ff]/20' : 'bg-zinc-800'}`}>
                                        <p className="text-zinc-400 mb-1">W{week}</p>
                                        <p className="text-white">{weekData.sets}x{weekData.reps}</p>
                                        <p className="text-zinc-500">{weekData.load}</p>
                                      </div>
                                    ) : null;
                                  })}
                                </div>
                                
                                {/* Coaching cues */}
                                {ex.coaching_cues?.length > 0 && (
                                  <div className="mt-2 flex flex-wrap gap-1">
                                    {ex.coaching_cues.map((cue, cueIdx) => (
                                      <span key={cueIdx} className="text-xs text-zinc-400 bg-zinc-800 px-2 py-1 rounded">
                                        {cue}
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Cool-down */}
                      {session.cool_down?.length > 0 && (
                        <div>
                          <p className="text-xs text-zinc-500 mb-2 uppercase tracking-wider">Cool-down</p>
                          <div className="space-y-1">
                            {session.cool_down.map((ex, exIdx) => (
                              <div key={exIdx} className="flex items-center justify-between text-sm p-2 bg-zinc-900/50 rounded">
                                <span className="text-white">{ex.exercise}</span>
                                <span className="text-zinc-400">{ex.duration}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Recovery Recommendations */}
          {planData.recovery_recommendations?.length > 0 && (
            <div className="mt-6 bg-green-500/10 border border-green-500/30 rounded-xl p-4">
              <h3 className="font-semibold text-green-400 mb-3">Recovery Recommendations</h3>
              <ul className="space-y-2">
                {planData.recovery_recommendations.map((rec, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm text-green-200">
                    <CheckCircle className="h-4 w-4 text-green-400 mt-0.5 shrink-0" />
                    {rec}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Progress Markers */}
          {planData.progress_markers?.length > 0 && (
            <div className="mt-4 bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
              <h3 className="font-semibold text-blue-400 mb-3">Progress Markers</h3>
              <ul className="space-y-2">
                {planData.progress_markers.map((marker, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm text-blue-200">
                    <TrendingUp className="h-4 w-4 text-blue-400 mt-0.5 shrink-0" />
                    {marker}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
